// src/pages/HrUpgradePage.js
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';

import { mentoringApi as axios, authApi } from '../axiosInstance';
import { ensureMentoringSession } from '../lib/session';

/* ============================== Modal ============================== */
// Inline Modal-Component für das Kontaktformular
function UpgradeContactModal({ open, onClose, user, currentPackage, targetPackage }) {
  const [form, setForm] = useState({
    name: ((user?.first_name || '') + ' ' + (user?.last_name || '')).trim(),
    email: user?.email || '',
    customer_id: user?.customer_id || '',
    current_package: currentPackage || '',
    target_package: targetPackage || '',
    message: '',
  });
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Wenn Props wechseln (z. B. anderes Zielpaket), Formular aktualisieren
    setForm(f => ({
      ...f,
      name: ((user?.first_name || '') + ' ' + (user?.last_name || '')).trim(),
      email: user?.email || '',
      customer_id: user?.customer_id || '',
      current_package: currentPackage || '',
      target_package: targetPackage || '',
    }));
  }, [user, currentPackage, targetPackage]);

  const handleChange = e => {
    setForm(f => ({ ...f, [e.target.name]: e.target.value }));
  };

  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true);

    // Platzhalter: Anfrage an eigene API übermitteln
    try {
      await fetch('/api/upgrade-request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      setSent(true);
      setLoading(false);
    } catch {
      setSent(false);
      setLoading(false);
    }
  }

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            className="bg-gray-900 text-white rounded-2xl p-8 max-w-md w-full shadow-2xl relative"
            initial={{ scale: 0.85, y: 60 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.9, y: 30, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 320, damping: 28 }}
          >
            <button
              onClick={onClose}
              className="absolute right-5 top-5 text-gray-400 hover:text-white text-xl"
              aria-label="Schließen"
            >
              ×
            </button>
            {!sent ? (
              <>
                <h2 className="text-xl font-bold mb-4">
                  Upgrade-Anfrage für{' '}
                  <span className="text-green-400">{targetPackage || '—'}</span>
                </h2>
                <form className="space-y-3" onSubmit={handleSubmit}>
                  <div>
                    <label className="text-sm">Name</label>
                    <input
                      type="text"
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                      className="w-full p-2 rounded bg-gray-800 mt-1"
                      required
                    />
                  </div>
                  <div>
                    <label className="text-sm">E-Mail</label>
                    <input
                      type="email"
                      name="email"
                      value={form.email}
                      onChange={handleChange}
                      className="w-full p-2 rounded bg-gray-800 mt-1"
                      required
                    />
                  </div>
                  <div>
                    <label className="text-sm">Kunden-ID</label>
                    <input
                      type="text"
                      name="customer_id"
                      value={form.customer_id}
                      onChange={handleChange}
                      className="w-full p-2 rounded bg-gray-800 mt-1"
                      disabled
                    />
                  </div>
                  <div>
                    <label className="text-sm">Aktuelles Paket</label>
                    <input
                      type="text"
                      name="current_package"
                      value={form.current_package}
                      disabled
                      className="w-full p-2 rounded bg-gray-800 mt-1"
                    />
                  </div>
                  <div>
                    <label className="text-sm">Zielpaket</label>
                    <input
                      type="text"
                      name="target_package"
                      value={form.target_package}
                      disabled
                      className="w-full p-2 rounded bg-gray-800 mt-1"
                    />
                  </div>
                  <div>
                    <label className="text-sm">Nachricht (optional)</label>
                    <textarea
                      name="message"
                      value={form.message}
                      onChange={handleChange}
                      className="w-full p-2 rounded bg-gray-800 mt-1"
                      rows={2}
                      placeholder="Optional"
                    />
                  </div>
                  <button
                    type="submit"
                    className="w-full bg-mentixxgreen text-black font-bold py-2 px-4 rounded-xl mt-2 hover:bg-green-400 transition disabled:opacity-50"
                    disabled={loading}
                  >
                    {loading ? 'Wird gesendet...' : 'Upgrade/Downgrade anfragen'}
                  </button>
                </form>
              </>
            ) : (
              <div className="text-center py-12">
                <div className="text-3xl mb-3">✅</div>
                <div className="text-lg font-semibold mb-1">Vielen Dank!</div>
                <div className="text-gray-400 mb-4">
                  Deine Anfrage wurde an das MΞNVIO Team übermittelt.
                </div>
                <button
                  className="bg-mentixxgreen px-4 py-2 rounded-full text-black mt-2 font-bold"
                  onClick={onClose}
                >
                  Schließen
                </button>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/* ============================== Daten / Helper ============================== */

const packages = [
  {
    id: 'MΞNVIO Essentials',
    price: '299,00 €/Monat (Laufzeit 12 Monate)',
    description: 'Starte smart – professionell matchen mit minimalem Aufwand',
    ideal: 'Pilotprojekte, erste Mentoring-Runden, kleine HR-Teams.',
    features: [
      'Bis zu 10 aktive Mentoring-Paare',
      '1 HR-Account',
      'Zugriff auf Matching, Bedarfserhebung, Kalender, Journal',
      'DSGVO-konform & sicher',
      'Support via E-Mail (48h Reaktionszeit)',
      '15 Minuten Setup – sofort startklar.',
    ],
    highlight: 'Essentials',
  },
  {
    id: 'MΞNVIO Growth',
    price: '899,00 €/Monat (Laufzeit 12 Monate)',
    description: 'Mehr Wirkung. Mehr Kontrolle. Mehr Mentoring-Erfolg.',
    ideal: 'Unternehmen mit mehreren Abteilungen oder Wachstumsplänen.',
    features: [
      'Bis zu 50 aktive Mentoring-Paare',
      '3 HR-Accounts',
      'Erweiterte Dashboards (KPIs, Zufriedenheit, Fortschritt)',
      'Automatisierte Feedbackzyklen (3, 6, 12 Monate)',
      'In-App & E-Mail-Benachrichtigungen',
      'Persönlicher Onboarding-Call inklusive',
      'Bietet mehr Klarheit durch Business-Impact-Tracking.',
    ],
    highlight: 'Growth',
  },
  {
    id: 'MΞNVIO Enterprise',
    price: '1.497,00 €/Monat (Laufzeit 12 Monate)',
    description: 'Vollintegration & Skalierung für datengetriebene HR',
    ideal: 'Unternehmen mit mehreren Standorten & hohem Qualitätsanspruch.',
    features: [
      'Bis zu 100 aktive Mentoring-Paare',
      '10 HR-Accounts',
      'Alle Features aus dem Growth-Paket',
      'Branding (Logo & Corporate Identity)',
      'API-Zugang (für Integrationen mit BI-Tools wie PowerBI, Tableau etc.)',
      'SSO/LDAP-Anbindung (Single Sign-On)',
      'SLA (Service Level Agreement) & direkter Produktsupport',
      'Auf Wunsch mit White Label & eigener Subdomain.',
    ],
    highlight: 'Enterprise',
  },
];

/** Axios-Pfad robust machen (wie auf Overview-Seite) */
function mPath(p = '') {
  const s = String(p || '');
  return s.replace(/^\/+/, '/').replace(/^\/mentoring(\/|$)/i, '/');
}

async function tryGet(client, url, timeoutMs = 4000) {
  try {
    const r = await client.get(mPath(url), { withCredentials: true, timeout: timeoutMs });
    return r?.data ?? null;
  } catch {
    return null;
  }
}

function normalizeProfile(raw) {
  if (!raw || typeof raw !== 'object') return {};
  const src = raw.user ?? raw.profile ?? raw.hr_profile ?? raw.data ?? raw;
  const customer = src.customer ?? src.tenant ?? {};
  const subscription = src.subscription ?? src.plan_info ?? {};
  return {
    first_name: src.first_name ?? src.firstName ?? src.given_name ?? '',
    last_name: src.last_name ?? src.lastName ?? src.family_name ?? '',
    email: src.email ?? src.mail ?? '',
    customer_id: src.customer_id ?? src.customerId ?? customer.id ?? src.tenant_id ?? '',
    package:
      src.package ??
      src.package_name ??
      src.plan ??
      subscription.package ??
      subscription.plan ??
      '',
  };
}

function normalizePackageFromAny(raw) {
  if (!raw || typeof raw !== 'object') return '';
  const src = raw.data ?? raw;
  const user = src.user ?? src.profile ?? src.hr_profile ?? {};
  const subscription = src.subscription ?? src.plan_info ?? src.current_subscription ?? {};
  const license = src.license ?? src.licenses ?? {};
  const product = src.product ?? {};
  const candidates = [
    src.package,
    src.package_name,
    src.plan,
    src.plan_name,
    src.tier,
    src.currentPackage,
    src.current_plan,
    user.package,
    user.package_name,
    user.plan,
    user.plan_name,
    subscription.package,
    subscription.plan,
    subscription.plan_name,
    subscription.tier,
    license.package,
    license.plan,
    license.plan_name,
    product.name,
    product.plan,
    product.package,
  ];
  for (const c of candidates) if (typeof c === 'string' && c.trim()) return c.trim();
  return '';
}

/** Mapping verschiedener Bezeichnungen auf die 3 Produktnamen */
function canonicalizePackageName(name) {
  const raw = String(name || '').trim();
  const s = raw.toLowerCase();

  if (!s) return '';

  // Robust gegen Schreibweisen wie "Growth-Paket", "Growth Paket", "Menvio Growth", etc.
  const norm = s
    .replace(/[\u2010-\u2015]/g, '-') // verschiedene Bindestriche → "-"
    .replace(/\s+/g, ' ') // Whitespace normalisieren
    .trim();

  // Essentials
  if (
    norm.includes('essentials') ||
    norm.includes('basic') ||
    norm.includes('starter') ||
    norm.includes('mentixx essentials') ||
    norm.includes('mΞnvio essentials') ||
    norm.includes('menvio essentials')
  ) {
    return 'MΞNVIO Essentials';
  }

  // Growth
  if (
    norm.includes('growth') ||
    norm.includes('firstmover') ||
    norm.includes('first mover') ||
    norm.includes('first-mover') ||
    norm.includes('growth-paket') ||
    norm.includes('growth paket') ||
    norm.includes('mentixx growth') ||
    norm.includes('mΞnvio growth') ||
    norm.includes('menvio growth')
  ) {
    return 'MΞNVIO Growth';
  }

  // Enterprise
  if (
    norm.includes('enterprise') ||
    norm.includes('pro') ||
    norm.includes('business') ||
    norm.includes('mentixx enterprise') ||
    norm.includes('mΞnvio enterprise') ||
    norm.includes('menvio enterprise')
  ) {
    return 'MΞNVIO Enterprise';
  }

  // Falls bereits exakt ein bekannter Name:
  const hit = packages.find(p => p.id.toLowerCase() === norm);
  return hit ? hit.id : raw;
}

/** Lokaler Fallback (z. B. vom Onboarding) */
function readLocalPackageFallback() {
  try {
    const keys = ['hr_invite_package', 'currentPackage', 'lastPackage'];
    for (const k of keys) {
      const v = localStorage.getItem(k);
      if (typeof v === 'string' && v.trim()) return v.trim();
    }
  } catch {}
  return '';
}

/* ============================== Page ============================== */

export default function HrUpgradePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { i18n } = useTranslation();

  // ⚠️ Hook nach oben gezogen (vor einem möglichen frühen return)
  const [modal, setModal] = useState({ open: false, target: null });

  const [user, setUser] = useState(null);
  const [active, setActive] = useState('');
  const [loading, setLoading] = useState(true);

  // aktuelles Paket (z. B. aus /hr/overview?current=…)
  const currentFromUrl = useMemo(
    () => canonicalizePackageName(searchParams.get('current') || ''),
    [searchParams]
  );

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        // Session sicherstellen (best effort)
        await ensureMentoringSession();

        // **1) Paket vom Mentoring-Server**
        const tp = await tryGet(axios, '/tenant-package', 5000);
        const pkgFromApi = canonicalizePackageName(normalizePackageFromAny(tp));

        // **2) Profildaten (Name, E-Mail, Kunden-ID, ggf. Paket)**
        let who = await tryGet(authApi, '/profile', 5000);
        if (!who?.user) who = await tryGet(authApi, '/whoami', 5000);
        if (!who?.user) who = await tryGet(axios, '/whoami', 5000);
        const prof = normalizeProfile(who);

        // **3) Quelle für „aktives Paket“ priorisieren**
        const localPkg = readLocalPackageFallback();
        const chosen =
          pkgFromApi ||
          canonicalizePackageName(prof.package) ||
          currentFromUrl ||
          canonicalizePackageName(localPkg) ||
          '';

        if (!alive) return;

        setUser({
          first_name: prof.first_name || '',
          last_name: prof.last_name || '',
          email: prof.email || '',
          customer_id: prof.customer_id || '',
          package: chosen || prof.package || '',
        });
        setActive(chosen);

        // für spätere Fallbacks merken
        try {
          if (chosen) localStorage.setItem('lastPackage', chosen);
        } catch {}
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [currentFromUrl]);

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white p-8">
        <p>Wird geladen …</p>
      </div>
    );
  }

  const backHref = `/hr/overview?lang=${encodeURIComponent(i18n?.language || 'de')}`;

  console.log('[HrUpgradePage] active=', active);
  console.log('[HrUpgradePage] package ids=', packages?.map(p => p.id));

  return (
    <div className="min-h-screen bg-black text-white p-8">
      <button
        onClick={() => navigate(backHref)}
        className="bg-green-400 text-black font-semibold px-4 py-2 rounded-full hover:bg-green-300 transition"
      >
        ← Zurück zum HR-Dashboard
      </button>

      <h1 className="text-3xl font-bold mt-6 mb-2">Upgrade dein MΞNVIO-Paket</h1>
      {active && (
        <p className="text-gray-300 mb-8">
          Aktuelles Paket:{' '}
          <span className="inline-block bg-gray-800 text-white px-3 py-1 rounded-full ml-1">
            {active}
          </span>
        </p>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {packages.map(pkg => {
          const isActive = pkg.id === active;
          return (
            <motion.div
              key={pkg.id}
              className={`rounded-3xl shadow-lg p-8 flex flex-col justify-between border-2 transition-all duration-300 ${
                isActive
                  ? 'border-mentixxgreen shadow-green-400/20 ring-2 ring-mentixxgreen bg-gray-900'
                  : 'border-gray-800 bg-gray-800 hover:ring-mentixxgreen/80'
              }`}
              whileHover={!isActive ? { scale: 1.03, boxShadow: '0 0 20px #1ede98' } : {}}
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              <div>
                <div className="text-xl font-bold mb-2">{pkg.id}</div>
                <div className="text-2xl font-bold text-green-400 mb-2">{pkg.price}</div>
                <div className="mb-1">{pkg.description}</div>
                <div className="mb-3 text-sm text-gray-400">
                  Ideal für: <span className="font-semibold text-white">{pkg.ideal}</span>
                </div>
                <ul className="mb-6 list-disc pl-5 space-y-1 text-sm text-gray-200">
                  {pkg.features.map((f, i) => (
                    <li key={i}>{f}</li>
                  ))}
                </ul>
                {isActive && (
                  <span className="inline-block bg-mentixxgreen text-black font-semibold text-xs px-3 py-1 rounded-full mb-4 shadow">
                    Aktives Paket
                  </span>
                )}
              </div>
              <div>
                {isActive ? (
                  <button
                    className="w-full bg-gray-700 text-white font-bold py-2 px-4 rounded-xl mt-2 opacity-60 cursor-not-allowed"
                    disabled
                  >
                    {pkg.id === 'MENTIXX Essentials' ? 'Downgrade nicht möglich' : 'Aktiv'}
                  </button>
                ) : (
                  <button
                    className="w-full bg-mentixxgreen text-black font-bold py-2 px-4 rounded-xl mt-2 hover:bg-green-400 transition"
                    onClick={() => setModal({ open: true, target: pkg.id })}
                  >
                    {pkg.id === 'MΞNVIO Essentials' && active !== 'MΞNVIO Essentials'
                      ? 'Downgrade anfragen'
                      : 'Upgrade anfragen'}
                  </button>
                )}
              </div>
            </motion.div>
          );
        })}
      </div>

      <UpgradeContactModal
        open={modal.open}
        onClose={() => setModal({ open: false, target: null })}
        user={user}
        currentPackage={active}
        targetPackage={modal.target}
      />
    </div>
  );
}
